import type { fabric } from "fabric"
import { markRaw } from "vue"
/**
 * 商品字段接口
 */

export type Color = {
  image?: string
  name: string
  value: string
  total?: number
}

export interface FontFile {
  path: string
  type: string
  img: string
}

export interface CustomFont {
  id: string
  name: string
  file_path: FontFile[]
  status: "enable" | "disable" // 状态
  sort_order: number
  font_type: "both" | "serif" | "sans" // 字体类型
  preview_image_path: string
  description: string
  license_type: string
  version: string
  tags: string[] | null
  createdAt: string // ISO 日期字符串
  updatedAt: string // ISO 日期字符串
}

export interface SelectTags {
  name?: string
  tag?: string
}

export interface TechnologyOptions {
  label: string
  value: string
  children: {
    disabled?: boolean
    label: string
    value: string
  }[]
}

export interface CouponByUser {
  activity_id: number
  claim_status: string
  claim_status_text: string
  coupon_code: string
  createdAt: string
  id: number
  order_id: number
  updatedAt: string
  user_id: string
}
export interface SingleCoupon {
  id: number
  /** 活动名称 */
  activity_name: string
  /** 活动类型 SKU category spu */
  activity_type: string
  /** 活动范围 这个券用在哪几个商品[] */
  activity_scope: any
  /** 活动状态 默认true false接口不显示该条 */
  is_active: boolean
  /** 是否能叠加 */
  is_stackable: boolean
  /** 限领人数 */
  max_participants?: number
  /** 减免金额 */
  discount_amount?: number
  /** 折扣率 */
  discount_rate?: number
  /** 满减门槛 */
  threshold_amount?: number
  /** 仅限新用户 */
  is_new_user_only?: boolean
  /** 新用户起始日期 */
  new_user_start_date?: string
  /** 活动开始时间 */
  start_time: string
  /** 活动结束时间 */
  end_time: string
  is_full: boolean // 是否已经领完
  ActivityUser?: CouponByUser
  disabled?: boolean
  hide?: boolean // 已被选择就不隐藏该券
  status?: number//  false 立即领取   1.立即使用    2.已失效
  commodityId?: number // 使用该卷的商品skuid
}

export type MaterialTag = {
  id: string
  index: number
  key: string
  name: string
}

export type MJImg = {
  groupId?: string
  id?: number
  imageUrl?: string
  prompt?: string
  taskId?: string
  subData?: MJImg[]
}

export interface Attr {
  id: number
  value: string
  object_value: any
  attribute: {
    key: string
  }
}

export type SimpleSku = {
  key: number
  color?: Color
  size: string
  code: string
  customCode?: number
  id: number
}

export interface selectType {
  value: string
  label: string
}

export type Technology = {
  id?: number
  name: string
  price: number
}

export interface ImageAltAttr {
  id: number
  value: string
  object_value: any
  spuAttr: {
    key: string
    attribute: {
      key: string
    }
  }
}

export type AltImage = {
  alt_image: string
  attrs?: Array<ImageAltAttr>
}

export type CustomizedAreasScale = {
  customable_image_id?: number
  discount_amount_scale: number
  customized_areas_scale: number
  title: string
  id?: number
}

export type CustomPrice = {
  id?: number
  name: string
  price: string
  customized_areas_scale: string
  customized_areas_scale_title: string
  discount_amount_scale: string
  technology: Technology
  technologies?: Array<Technology>
  dss_image_price?: string
  deletedAt?: string
}

export type CustomAreaImgHistory = {
  history: string,
  segmented: string
}

export type BoundingRect = {
  left: number
  top: number
  width: number
  height: number
}

export type CustomAreas = {
  title?: string
  id?: number
  left: number
  top: number
  width: number
  height: number
  scale: number
  dpi: number
  bgHeight: number
  bgWidth: number
  templateWidth: number
  templateHeight: number
}

export type CustomableImage = {
  // color?: string
  id: number
  name: string
  image: string
  index: null
  width: number
  height: number
  spu_id: number
  custom_image: string
  price: number
  scale: number
  scales: Array<CustomizedAreasScale>
  custom_areas: CustomAreas
  altImages?: AltImage[]
}
export interface Spu {
  id?: number
  name?: string
  code?: string
  desc?: string
  status?: 0
  price: number
  sale_count: string
  deliver: string
  category_id?: number
  image?: string
  images: [{ name: string; url: string }]
  attrs?: Array<Attr>
  technology: string
  skus: {
    id: number
  }[]
  customableImages?: Array<CustomableImage>
  threeDimensional: Array<{
    id: number
    model: string
    preview: boolean
    title: string
    corresponding: Array<{
      customImageId: number
      name: string
    }>
  }>
}

export type RawCustomizedImage = {
  price: number
  customized_image: string
  customableImage: CustomableImage
  customized_json: any
  customized_areas?: object
}

export type CustomPriceDetail = {
  custom_price: Array<CustomPrice> | []
  discount_amount_scale?: string
  customized_areas?: object
  customized_areas_scale_title?: string
  sku_price?: number | null
  spu_price: number
  spu_discount: {
    express: {
      value: number,
      name: string
    },
    clothing: {
      value: number,
      name: string
    },
    design: {
      value: number,
      name: string
    }
  } | any
}

export type CustomizedImage = {
  color?: Color
  discount_amount_scale?: string
  customized_image: string
  customized_areas_scale: string
  technologies?: Technology[]
  boundingRect?: { left: number, top: number, width: number, height: number }
} & CustomableImage

export interface RelevanceNodes {
  customImageId: number
  node: string
}

export interface RelevanceSwatch {
  customImageId: number
  texture: string
  currentLocation: string
}

export interface ThreeDimensionalModelInfo {
  id?: number
  relevanceSwatch: Array<RelevanceSwatch>
  color?: string
  url: string
  relevanceNodes?: Array<RelevanceNodes>
}

export interface CustomAreaAdjustment {
  flipHorizontal: boolean // 水平翻转
  flipVertical: boolean // 垂直翻转
  fontBold: boolean // 加粗
  linethrough: boolean // 中线
  underline: boolean // 下划线
  fontItalic: boolean // 斜体
  rotate: number // 旋转
  fontColor: string // 字体颜色
  fontFamily: string // 字体
  fontSize: number // 字体大小
  strokeWidth: number // 描边颜色
  strokeColor: string // 描边粗细
  textAlign: string // 对齐方式
  maximize: boolean // 最大化
  spreadOut: boolean // 平铺
  heightMaximization: boolean // 高度最大化
  widthMaximization: boolean; // 宽度最大化
  [key: string]: boolean | number | string | undefined
}

export class EditingImage {
  json: any
  image: CustomizedImage
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fabric: any
  canvasDom?: fabric.Canvas | null | object
  constructor(image: CustomizedImage) {
    this.image = image
    this.fabric = null
    this.canvasDom = null
  }

  onReady = (value?: fabric.Canvas) => {
    this.canvasDom = markRaw(value as fabric.Canvas)
  };
}

export interface DssCustomizedImage {
  id?: number
  dss_theme_id: number
  image: string
  ref_price: number
}

export interface DssTheme {
  id?: number
  status: number
  price: number
  title: string
  tags: string
  tenant: string
  desc: string
  shareable: boolean
  free: boolean
  customable: boolean
  discountable: boolean
  discountRate: number
  editable: boolean
  username: string
  orderStatus: null | number
  type?: null | string
  orderDetails?: {
    status: number
    type: number
  }[]
  dssCustomizedImage: DssCustomizedImage[]
}

export interface UserDssCustomizedImage extends DssCustomizedImage {
  theme: DssTheme
}

export interface DssThemeTag {
  tag: string
  dssThemeList: DssTheme[]
  // 待用 防止一次性拉的数据太多 页面卡顿 限制每次展示20个 点更多以后展示
  showNumber: number
}


export interface BoughtDssOrder {
  id: number
  price: string
  pay_at: string
  pay_type: string
  type: string
  theme: DssTheme
}

export interface BuyDssTheme {
  dsstheme: DssTheme | undefined
  selectedCustomizedImages: DssCustomizedImage[]
  buyWholeSet?: boolean
}

export interface SelectedDssThemeList {
  product_spu_customable_image_id?: number
  buyDssThemeList: BuyDssTheme[]
}

export interface ExternalProcurementBasicParameter {
  color: {
    name: string
    value: string
    label?: string
  }
  size: string
  quality: number
  // 让每个sku都有自己的默认光标
  selectedTechnology?: string[]
  technology: {
    customableImageName: string
    name: string
  }[]
}

export interface NewCustomSpu {
  spu_id?: number
  colors?: Color[]
  price?: number
  name?: string
  baseData?: ExternalProcurementBasicParameter[]
  customizedImages?: {
    custom_area_id: number
    spu_customable_image_id: number
    customized_image: string
    customized_json?: any
    customized_areas?: object
  }[]
  selectedDssThemeList?: BuyDssTheme
}

export interface NewExternalCustomSpu {
  spu_id?: number
  colors?: Color[]
  price?: number
  name?: string
  customizedImages?: {
    customized_image: string
    spu_customable_image: string
    customized_json?: any
    customized_areas?: object
  }[]
  selectedDssThemeList?: BuyDssTheme
}

export type ExternalCustomableImage = {
  id: number
  name: string
  image: string
  width: number
  height: number
  custom_areas: CustomAreas
  spu_id?: number
  price?: number
}

export type CartDataSku = {
  code: string
  id: number
  color?: Color
  size?: string
  sku?: {
    id: number
    name: string
    attrs: [
      {
        value: any
        object_value: any
        attribute: {
          key: string
        }
      }
    ]
  }
  attrs?: [
    {
      value: any
      object_value: any
      attribute: {
        key: string
      }
    }
  ]
}

export type SimpleSpu = {
  colors: Array<Color>
  sizes: Array<string>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customSkus: SimpleSku[]
}

export interface CustomSku {
  code: string
  id: number
  color?: Color
  size?: string
  sku: {
    price?: number
    name: string
    attrs: [
      {
        value: any
        object_value: any
        attribute: {
          key: string
        }
      }
    ]
  }
}

export interface Reference {
  id: number
  custom_spu_id: number
  dss_customized_image_id: number
  dss_theme_id: number
  type: string
  tenant: string
  customizedImage: DssCustomizedImage
}

export type CartData = {
  is_custom: boolean
  spu_id: number
  name: string
  spu_name: string
  image: any
  customizedImages: RawCustomizedImage[]
  skus: CartDataSku[]
  custom_spu_id: number
  customSkus?: CustomSku[]
  price_detail: CustomPriceDetail
  references: Reference
}

export interface CustomSpu {
  id: number
  name: string
  createdAt: string
  spu_id: number
  price: number
  code: number
  customSkus: CustomSku[]
  customizedImages: RawCustomizedImage[]
  spu: Spu
  price_detail: CustomPriceDetail
  references: Reference
}

export interface SpuImage {
  createdAt: string
  deletedAt: string
  id: number
  image: string
  index: number
  name: string
  spu_id: number
  updatedAt: string
}

export interface ShoppingCartItem {
  quality: number // 数量
  spu_customable_image: string // pms_product_spu_customable_image[0]
  spu_name: string // pms_product_spu.name
  custom_spu_name: string // pms_custom_spu.name
  image: string // 背景图
  sku_code: string
  id: number
  sku_attrs: string
  category_id: number
  spu_custom_width: number
  spu_custom_height: number
  spu_custom_areas: CustomAreas
  custom_sku_id: number // 用户自定义商品的ID
  custom_spu_id: number
  sku_id: number | string // 用户自定义商品的ID
  spu_id: number
  isRemoved: boolean  // spu sku 以及 设计主题的下架管理 
  price: string
  is_custom: boolean
  price_detail: CustomPriceDetail
  technology: Technology[]
  selectCoupon?: Array<any>
  dssData: Reference[]
}
export interface ShoppingCart {
  items: ShoppingCartItem[]
}

export interface CouponCondition {
  commodityId: number
  text: string
}

/** 素材分类 */
export interface MaterialItem {
  id: number
  name: string
  category_id: number
  image: string
  specifications: string
  tag: string
}

export interface AdjustItem {
  pay_amount: number,
  original_amonut: number,
  rate: number,
  express_amount?: number
}
